<template>
  <div class="modal-overlay">
    <div class="modal-content">
      <h3 class="mb-4 text-center">
        Complete Your Profile
      </h3>
      <p class="text-muted text-center mb-4">
        Please fill in the missing details to post an ad.
      </p>

      <!-- SPAM Warning State -->
      <div
        v-if="showSpamWarning"
        class="text-center"
      >
        <div class="alert alert-warning mb-4 text-start">
          <div class="text-center mb-3">
            <i
              class="fas fa-exclamation-triangle text-warning"
              style="font-size: 32px;"
            />
          </div>
          <h5 class="text-center">
            Communication Warning
          </h5>
          <p>We can only reach you via Email. Emails can sometimes fall into SPAM, and you might miss messages from interested people.</p>
          <p class="mb-0 text-center">
            <strong>We strongly recommend enabling Push messages or providing a phone number.</strong>
          </p>
        </div>
        <div class="d-flex justify-content-between gap-3">
          <button
            type="button"
            class="btn btn-outline-secondary w-50"
            @click="showSpamWarning = false"
          >
            Go Back
          </button>
          <button
            type="button"
            class="btn btn-warning w-50"
            :disabled="saving"
            @click="proceedWithSave"
          >
            <span
              v-if="saving"
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
            Proceed Anyway
          </button>
        </div>
      </div>

      <form
        v-else
        @submit.prevent="handleSave"
      >
        <!-- Name Input -->
        <div
          v-if="!hasDisplayName"
          class="form-group mb-3"
        >
          <label>Full Name <span class="text-danger">*</span></label>
          <input
            v-model="formData.displayName"
            type="text"
            class="form-control"
            placeholder="e.g. John Doe"
            required
          >
        </div>

        <!-- Postal Code System -->
        <div class="form-group mb-3">
          <label>Postal Code <span class="text-danger">*</span></label>
          <div class="input-group">
            <input
              v-model="postalCode"
              type="text"
              class="form-control"
              placeholder="e.g. IG10 2QS"
              required
            >
            <button
              type="button"
              class="btn border-0 text-white"
              :style="isValidPostalCode ? 'background-color: #81c784 !important;' : 'background-color: #e57373 !important;'"
              :disabled="postalCheckLoading"
              @click="checkPostalCode"
            >
              <span
                v-if="postalCheckLoading"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              CHECK
            </button>
          </div>
          
          <div
            v-if="postalError"
            class="text-danger mt-1 small"
          >
            {{ postalError }}
          </div>
          
          <div
            v-if="isValidPostalCode && suggestedAddress"
            class="text-success mt-2 small"
          >
            <i class="fas fa-check-circle" /> Address found: {{ suggestedAddress }}
          </div>
        </div>

        <!-- Phone Number -->
        <div class="form-group mb-4">
          <label>Phone Number (Optional)</label>
          <input
            v-model="formData.phoneNumber"
            type="tel"
            class="form-control"
            placeholder="e.g. +44 123 456 7890"
          >
          <small class="form-text text-muted">You can hide this on your ad and receive SMS notifications.</small>
        </div>

        <!-- Push Notifications -->
        <div
          v-if="!formData.phoneNumber && !pushNotificationsEnabled"
          class="form-group mb-4 d-flex flex-column align-items-center bg-light p-3 rounded"
        >
          <p class="small text-center mb-2">
            Don't want to share your phone number? Enable push notifications to stay updated.
          </p>
          <button
            type="button"
            class="btn btn-sm text-white"
            style="background-color: #3b82f6 !important; border: none;"
            @click="requestPushPermission"
          >
            <i class="fas fa-bell" /> ENABLE NOTIFICATIONS
          </button>
        </div>
        <div
          v-if="pushNotificationsEnabled"
          class="form-group mb-4 text-center text-success"
        >
          <i class="fas fa-bell" /> Push notifications enabled
        </div>

        <div
          v-if="formError"
          class="alert alert-danger py-2 small text-center mb-3"
        >
          {{ formError }}
        </div>
        <div
          v-if="successMessage"
          class="alert alert-success py-2 small text-center mb-3"
        >
          {{ successMessage }}
        </div>

        <!-- Save Button -->
        <div class="d-grid gap-2">
          <button
            type="submit"
            class="btn border-0 text-white"
            :style="isValidPostalCode ? 'background-color: #3b82f6 !important;' : 'background-color: #e57373 !important;'"
            :disabled="saving || !isValidPostalCode"
          >
            <span
              v-if="saving"
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
            {{ isValidPostalCode ? 'Save & Continue' : 'Please enter postal code and click CHECK' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { usePostalCodeManager } from '@/composables/usePostalCodeManager';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { getAuth } from 'firebase/auth';

defineProps({
  isVisible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['completed', 'close']);

const userStore = useUserStore();
const saving = ref(false);
const formError = ref('');
const successMessage = ref('');
const showSpamWarning = ref(false);

const formData = ref({
  displayName: '',
  phoneNumber: ''
});

const pushNotificationsEnabled = ref(false);
const hasDisplayName = computed(() => !!userStore.userData?.displayName);

const {
  postalCode,
  loading: postalCheckLoading,
  error: postalError,
  isValidPostalCode,
  suggestedAddress,
  validatePostCode,
  latitude,
  longitude,
  postalTown,
  route
} = usePostalCodeManager();

onMounted(() => {
  if (userStore.userData) {
    formData.value.displayName = userStore.userData.displayName || '';
    formData.value.phoneNumber = userStore.userData.phoneNumber || '';
    pushNotificationsEnabled.value = userStore.userData.pushNotificationsEnabled || false;
    
    if (userStore.userData.postalCode) {
      postalCode.value = userStore.userData.postalCode;
      // Otomatik check yapabiliriz
      checkPostalCode();
    }
  }
});

const checkPostalCode = async () => {
  if (!postalCode.value) return;
  await validatePostCode(postalCode.value);
};

const requestPushPermission = async () => {
  formError.value = '';
  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      pushNotificationsEnabled.value = true;
    } else {
      formError.value = 'Notification permission denied.';
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    formError.value = 'Error requesting notification permission.';
  }
};

const handleSave = async () => {
  formError.value = '';
  
  if (!isValidPostalCode.value) {
    formError.value = 'Please check your postal code before saving.';
    return;
  }

  if (!hasDisplayName.value && !formData.value.displayName) {
    formError.value = 'Please enter your full name.';
    return;
  }

  // SPAM Warning Logic
  if (!formData.value.phoneNumber && !pushNotificationsEnabled.value) {
    showSpamWarning.value = true;
    return;
  }

  await proceedWithSave();
};

const proceedWithSave = async () => {
  saving.value = true;
  formError.value = '';

  try {
    const auth = getAuth();
    const userRef = doc(db, 'users', auth.currentUser.uid);
    
    const updates = {
      postalCode: postalCode.value,
      latitude: latitude.value,
      longitude: longitude.value,
      postalTown: postalTown.value,
      route: route.value,
      pushNotificationsEnabled: pushNotificationsEnabled.value
    };

    // Construct full address from available parts
    const addressParts = [];
    if (route.value) addressParts.push(route.value);
    if (postalTown.value) addressParts.push(postalTown.value);
    if (addressParts.length > 0) {
      updates.address = addressParts.join(', ');
    }

    if (!hasDisplayName.value) {
      updates.displayName = formData.value.displayName;
    }
    
    if (formData.value.phoneNumber) {
      updates.phoneNumber = formData.value.phoneNumber;
    }

    await updateDoc(userRef, updates);
    
    // Update local store: clear cache flag and fetch full profile
    userStore.isProfileLoaded = false;
    await userStore.fetchUserProfile(auth.currentUser.uid);

    successMessage.value = 'Profile completed successfully!';
    
    // Briefly show the success message before closing
    setTimeout(() => {
      emit('completed');
    }, 1000);
  } catch (error) {
    console.error('Error updating profile:', error);
    formError.value = 'An error occurred while saving. Please try again.';
    saving.value = false;
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

.modal-content {
  background-color: #ffffff;
  padding: 2rem;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
}
</style>
